import { Component, input, Output, OnInit, EventEmitter } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { IntegrationUnit, UnitMapping } from '@/core/models/IntegrationEntity';
import { DialogModule } from 'primeng/dialog';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { firstValueFrom } from 'rxjs';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToastService } from '@/core/services/toast.service';
import { CloneUtils } from '@/core/utils/clone-utils';
import { MessageModule } from 'primeng/message';
import { IntegrationCardData } from '@/home/settings/integrations/integration-card/integration-card.types';

type TableUnits = {
    key: string;
    unit?: IntegrationUnit;
    multiplier: string;
};

/**
 * A positive decimal number followed by the hour or minute unit, e.g. "8h", "0.5h" or "15m".
 *
 * Anchored on purpose: the multiplier is stored verbatim and the API converts invoice quantities
 * with it, so anything the pattern lets through has to mean exactly one thing.
 */
const MULTIPLIER_PATTERN = /^\d+(\.\d+)?[hm]$/;

@Component({
    selector: 'app-integration-unit-mapping',
    standalone: true,
    imports: [
        TooltipModule,
        TranslatePipe,
        DialogModule,
        TableModule,
        SelectModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        MessageModule,
    ],
    templateUrl: './integration-unit-mapping.component.html',
    styleUrls: ['./integration-unit-mapping.component.scss'],
})
export class IntegrationUnitMappingComponent implements OnInit {
    integration = input.required<IntegrationCardData>();
    missingUnits = input<string[] | undefined>();
    // Dataflow note:
    // This component does not handle the change (update operation) on the integration settings
    // It only notifies the parent component about the change, which performs the update operation
    // and by this updates the integration input parameter.
    @Output() onUnitsChanged = new EventEmitter<UnitMapping>();
    units?: IntegrationUnit[];
    unitSelectOptions: IntegrationUnit[] = [];
    mappedUnits: UnitMapping = {};
    selectedUnit?: string;
    inputUnit: string = '';
    inputMultiplier: string = '';
    inputMultiplierStyle: string = '';
    tableUnits: TableUnits[] = [];
    dataInitialized: boolean = false;
    readyToAdd = false;

    public constructor(
        private readonly integrationService: IntegrationService,
        private readonly toastService: ToastService,
    ) {}

    public ngOnInit(): void {
        void this.initUnits();
    }

    private async initUnits(): Promise<void> {
        const config = this.integration().integration!;
        const supportsUnitRequest = config.properties?.supportsUnitRequest ?? false;
        const authValid = config.authStatus === 'valid';
        if (supportsUnitRequest && authValid) {
            this.units = await firstValueFrom(this.integrationService.getUnits(config.service));
        }
        this.initTableAndForm();
        this.dataInitialized = true;
    }

    private updateTable(): void {
        const tableUnits: TableUnits[] = [];
        for (const [key, value] of Object.entries(this.mappedUnits)) {
            tableUnits.push({ key, multiplier: value });
        }
        this.tableUnits = tableUnits;
    }

    private updateSelectOptions(): void {
        if (!this.units) {
            return;
        }
        const unitsCopy = CloneUtils.cloneData(this.units);
        const alreadyMappedUnits = Object.keys(this.mappedUnits);
        this.unitSelectOptions = unitsCopy.filter((unit) => !alreadyMappedUnits.includes(unit.name));
    }

    private resetInputFields(): void {
        this.selectedUnit = undefined;
        this.inputUnit = '';
        this.inputMultiplier = '';
    }

    public removeUnit(key: string): void {
        if (key in this.mappedUnits) {
            delete this.mappedUnits[key];
        } else {
            this.toastService.toast('UNEXPECTED_ERROR');
        }
        this.updateTable();
        this.updateSelectOptions();
        this.emitChange();
    }

    public addUnit(): void {
        if (!this.selectedUnit && !this.inputUnit) {
            return;
        }
        const key = this.selectedUnit ?? this.inputUnit;
        const multiplier = this.inputMultiplier.trim();

        if (key in this.mappedUnits) {
            this.toastService.toast('STANDARD_ERROR');
            return;
        }
        this.mappedUnits[key] = multiplier;
        this.updateTable();
        this.updateSelectOptions();
        this.resetInputFields();
        this.emitChange();
        this.readyToAdd = false;
    }

    private emitChange(): void {
        this.onUnitsChanged.emit(CloneUtils.cloneData(this.mappedUnits));
    }

    public checkReady(): void {
        const hasUnit = !!(this.selectedUnit || this.inputUnit);
        const hasMultiplier = !!this.inputMultiplier;
        let validMultiplier = false;

        if (hasMultiplier) {
            validMultiplier = this.validateMultiplier();
            if (validMultiplier) {
                this.inputMultiplierStyle = '';
            } else {
                this.inputMultiplierStyle = 'border: 1px solid red;';
            }
        }

        this.readyToAdd = hasUnit && hasMultiplier && validMultiplier;
    }

    private validateMultiplier(): boolean {
        // Trimmed like the API trims it, so both sides agree on what the stored value means.
        const multiplier = this.inputMultiplier.trim();

        if (!MULTIPLIER_PATTERN.test(multiplier)) {
            return false;
        }

        const numericMultiplier = Number.parseFloat(multiplier.slice(0, -1));
        return Number.isFinite(numericMultiplier) && numericMultiplier > 0;
    }

    private initTableAndForm(): void {
        const config = this.integration().integration!;
        this.mappedUnits = {};
        this.selectedUnit = undefined;
        const unitMap = config.configuration?.unitMapping;
        // TODO define what behavior has better UX:
        //      always prefill or only prefill with 1h when data is initial and not modified by the user
        if (unitMap /* && Object.keys(unitMap).length > 0 */) {
            this.mappedUnits = CloneUtils.cloneData(unitMap);
        } else if (config.properties?.hourUnitName) {
            this.mappedUnits[config.properties.hourUnitName] = '1h';
        }
        this.updateTable();
        this.updateSelectOptions();
        this.resetInputFields();
    }
}
